import { useMemo, useState } from "react";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { bildirimOlayAdi } from "@koclukpanel/contracts";
import { BosDurum, Buton, Iskelet, OnayKutusu, Rozet, Secim } from "@koclukpanel/ui";
import {
  ApiHatasi,
  bildirimHepsiOkundu,
  bildirimlerim,
  bildirimOkundu,
  type Bildirim,
} from "./api";
import { bildirimHedefi } from "./bildirim-yonlendirme";

/**
 * BİLDİRİMLERİM — F-NTF-09, öğrenci ve veli tarafı
 *
 * ================================================================
 * BU EKRANIN VAR OLMA SEBEBİ
 * ================================================================
 * Şartnamenin kendi cümlesi: *"Aynı gelen kutusu öğrenci
 * uygulamasında da vardır; öğrencinin bildirimi telefonda kalır."*
 *
 * Panel tarafı 04.09.2026'da yazıldı, bu taraf yazılmadı ve
 * `05-gorev-listesi.md` içinde açık madde olarak duruyordu. Yani
 * bugüne kadar öğrenciye ve veliye giden panel içi bildirimler —
 * *"hesabınıza yeni bir cihazdan girildi"* dahil — hiçbir yerde
 * görülemiyordu.
 *
 * ================================================================
 * "YENİ CİHAZDAN GİRİŞ" BU EKRANIN EN AĞIR SATIRI
 * ================================================================
 * Ölçüldü (07.09.2026): bugün öğrenci/veli hesabına gerçekten üretilen
 * bildirimler `YeniCihazGirisi` (G-AUT-07), `VeliRaporuYayinlandi`,
 * `MemnuniyetAnketiGonderildi` ve `MuvafakatEksik`.
 *
 * Birincisi bir GÜVENLİK bildirimi ve tek gösterildiği yer burası:
 * hesabı ele geçirilen bir öğrencinin bunu öğrenebileceği başka bir
 * yüzey yok. "Bildirim penceresi eksik" bu yüzden bir konfor maddesi
 * değil.
 *
 * ================================================================
 * SÜZGEÇLERİN NEREDE ÇALIŞTIĞI
 * ================================================================
 * "Yalnızca okunmamış" SUNUCUDA — uç zaten destekliyor ve rozet
 * sayacı da aynı süzgeci kullanıyor.
 *
 * Tarih aralığı ve olay türü İSTEMCİDE, yalnızca ÇEKİLMİŞ sayfalar
 * üzerinde. Bu bir kusur ve bilerek böyle: telefonda her süzgeç
 * değişiminde ağ turu atmak, mobil veriyle gezen bir öğrenciye
 * ödetilecek bir maliyet. "Daha eskisini yükle" düğmesi süzgeç boş
 * sonuç verse bile duruyor — aradığı bildirim daha eski bir sayfada
 * olabilir.
 */

/**
 * DİL KİPİ — öğrenciye "sen", veliye "siz".
 *
 * `Mesajlar.tsx` ile aynı desen ve aynı gerekçe (`VeliKabugu.tsx`:
 * *"Veli öğrenci değil."*). Sözlük olarak duruyor ki bir metin
 * eklerken ötekini yazmayı unutmak DERLEME hatası versin.
 *
 * Sözlükte YALNIZCA kipe göre değişen metinler var: "Tümünü okundu
 * işaretle" ya da "Bu süzgeçte bildirim yok" iki kipte de aynı ve
 * kopyalamak, birini düzeltip ötekini geride bırakma riskini bedavaya
 * satın almak olurdu.
 */
type Kip = "sen" | "siz";

interface KipMetni {
  bosBaslik: string;
  bosAciklama: string;
  okunmamisYok: string;
}

const METIN: Record<Kip, KipMetni> = {
  sen: {
    bosBaslik: "Henüz bildirimin yok",
    bosAciklama:
      "Programın yayınlandığında, deneme sonucun hazır olduğunda ya da hesabına yeni bir cihazdan girildiğinde burada görünür.",
    okunmamisYok: "Okunmamış bildirimin yok",
  },
  siz: {
    bosBaslik: "Henüz bildiriminiz yok",
    bosAciklama:
      "Çocuğunuzun gelişim raporu yayınlandığında, bir anket gönderildiğinde ya da hesabınıza yeni bir cihazdan girildiğinde burada görünür.",
    okunmamisYok: "Okunmamış bildiriminiz yok",
  },
};

const ARALIK = [
  ["hepsi", "Tüm zamanlar"],
  ["7", "Son 7 gün"],
  ["30", "Son 30 gün"],
] as const;

type Aralik = (typeof ARALIK)[number][0];

function tarihSaat(iso: string): string {
  return new Date(iso).toLocaleString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function Bildirimlerim({
  kip = "sen",
  onGeri,
  onOkundu,
  onGit,
}: {
  kip?: Kip;
  /**
   * Geri düğmesi YALNIZCA çağıran isterse.
   *
   * Öğrencide bu ekran `Hesabım`ın alt ekranı ve geri düğmesi ORADA
   * duruyor; iki tane çizmek, ikisinin farklı yerlere götürdüğünü
   * düşündürürdü. Veli portalında ekranı devralıyor, o yüzden kendi
   * düğmesi gerekiyor. `Mesajlar.tsx` ile aynı kural.
   */
  onGeri?: () => void;
  /**
   * Bir bildirim okundu — kabuğun rozeti tazelensin.
   *
   * Sayaç bu ekranın DIŞINDA ve 60 saniyede bir dönüyor; okuma anında
   * haber vermeseydik rozet, kullanıcı bildirimi okuduktan sonra bir
   * dakika daha durur ve gördüğüyle çelişirdi. Mesaj rozetinde aynı
   * kusur 06.09.2026'da canlı ölçülmüştü.
   */
  onOkundu?: () => void;
  /**
   * Bildirimi doğuran kayda götürür — F-NTF-09.
   *
   * Verilmezse "Kayda git" düğmesi HİÇ çizilmiyor. Öğrenci kabuğunda
   * bugün verilmiyor: `bildirim-yonlendirme.ts` bu uygulamada yalnızca
   * veli raporunu tanıyor ve öğrencinin gidebileceği bir kayıt yok.
   */
  onGit?: (hedef: { gorunum: "veli-rapor"; kayitId: string }) => void;
} = {}) {
  const qc = useQueryClient();
  const [yalnizOkunmamis, setYalnizOkunmamis] = useState(false);
  const [aralik, setAralik] = useState<Aralik>("hepsi");
  const [olay, setOlay] = useState("");

  const liste = useInfiniteQuery({
    queryKey: ["bildirimlerim", yalnizOkunmamis],
    queryFn: ({ pageParam }) => bildirimlerim(yalnizOkunmamis, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (son) => son.sonrakiImlec ?? undefined,
    retry: false,
  });

  const tumKayitlar = useMemo(
    () => (liste.data?.pages ?? []).flatMap((p) => p.bildirimler),
    [liste.data],
  );

  const tazele = async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: ["bildirimlerim"] });
    // Rozet sayacı AYRI bir uçtan geliyor ve bu ekranın dışında
    // yaşıyor; haber vermeseydik "okudum ama rozet düşmüyor" olurdu.
    onOkundu?.();
  };

  const okundu = useMutation({
    mutationFn: (id: string) => bildirimOkundu(id),
    onSuccess: tazele,
  });

  const hepsi = useMutation({
    mutationFn: bildirimHepsiOkundu,
    onSuccess: tazele,
  });

  const olayTurleri = useMemo(() => {
    const kodlar = new Set(tumKayitlar.map((b) => b.eventCode));
    return [...kodlar].sort((a, b) => bildirimOlayAdi(a).localeCompare(bildirimOlayAdi(b), "tr"));
  }, [tumKayitlar]);

  const gorunen = useMemo(() => {
    const sinir = aralik === "hepsi" ? null : Date.now() - Number(aralik) * 24 * 60 * 60 * 1000;
    return tumKayitlar.filter(
      (b) =>
        (olay === "" || b.eventCode === olay) &&
        (sinir === null || new Date(b.createdAt).getTime() >= sinir),
    );
  }, [tumKayitlar, aralik, olay]);

  const okunmamisSayisi = tumKayitlar.filter((b) => !b.isRead).length;

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex items-center gap-2">
        {onGeri && (
          <Buton gorunum="sessiz" onClick={onGeri}>
            ← Geri
          </Buton>
        )}
        <h1 className="text-notr-900 text-lg font-semibold">Bildirimler</h1>
      </div>

      {liste.isPending ? (
        <Iskelet satir={4} />
      ) : liste.isError ? (
        <BosDurum
          ton="hata"
          baslik="Bildirimler alınamadı"
          aciklama={liste.error instanceof ApiHatasi ? liste.error.message : undefined}
          eylem={
            <Buton gorunum="ikincil" onClick={() => void liste.refetch()}>
              Tekrar dene
            </Buton>
          }
        />
      ) : (
        <>
          {/*
            SÜZGEÇLER SARIYOR. 390px'te iki seçim yan yana sığmıyor;
            `flex-wrap` ile alt alta düşüyorlar. Seçimler `w-full
            sm:w-auto`: dar ekranda tam genişlik, geniş ekranda yan
            yana.
          */}
          <div className="flex flex-wrap items-end gap-2">
            <Secim
              etiket="Tarih"
              value={aralik}
              onChange={(e) => setAralik(e.target.value as Aralik)}
              className="w-full sm:w-44"
            >
              {ARALIK.map(([a, e]) => (
                <option key={a} value={a}>
                  {e}
                </option>
              ))}
            </Secim>

            <Secim
              etiket="Tür"
              value={olay}
              onChange={(e) => setOlay(e.target.value)}
              className="w-full sm:w-56"
            >
              <option value="">Bütün bildirimler</option>
              {olayTurleri.map((k) => (
                <option key={k} value={k}>
                  {bildirimOlayAdi(k)}
                </option>
              ))}
            </Secim>

            {/*
              HAM `<input type="checkbox">` DEĞİL: elle yazılan kutunun
              odak biçimi olmuyor ve etiketi `htmlFor` ile bağlı
              olmadığı için dokunma hedefi kutunun kendisi kadar
              kalıyor — telefonda 16px'lik bir hedef.
            */}
            <OnayKutusu
              etiket="Yalnızca okunmamış"
              checked={yalnizOkunmamis}
              onChange={(e) => setYalnizOkunmamis(e.target.checked)}
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-notr-600 text-sm">
              {gorunen.length} / {tumKayitlar.length} bildirim
              {/*
                DAHA ESKİSİ VARSA SÖYLENİYOR. "20 / 20" yazıp susmak,
                kullanıcıya hepsinin bu olduğunu söylerdi.
              */}
              {liste.hasNextPage && <span className="text-notr-600"> · daha eskisi var</span>}
            </span>

            {okunmamisSayisi > 0 && (
              <div className="ml-auto">
                <Buton
                  gorunum="ikincil"
                  yukleniyor={hepsi.isPending}
                  onClick={() => hepsi.mutate()}
                >
                  Tümünü okundu işaretle
                </Buton>
              </div>
            )}
          </div>

          {(okundu.isError || hepsi.isError) && (
            <p role="alert" className="text-hata-500 text-sm font-medium">
              {(okundu.error ?? hepsi.error) instanceof ApiHatasi
                ? ((okundu.error ?? hepsi.error) as ApiHatasi).message
                : "İşlem tamamlanamadı."}
            </p>
          )}

          {gorunen.length === 0 ? (
            /*
              BOŞUN İKİ TÜRÜ. Süzgeç daralttıysa çözüm "süzgeci
              temizle"; gerçekten bildirim yoksa yapacak bir şey yok
              ve bunu söylemek gerekiyor — boş bir liste, bozuk bir
              liste gibi görünür.
            */
            tumKayitlar.length > 0 ? (
              <BosDurum
                ikon="bos"
                baslik="Bu süzgeçte bildirim yok"
                aciklama="Bildirim var ama süzgeç daralttı."
                eylem={
                  <Buton
                    gorunum="ikincil"
                    onClick={() => {
                      setAralik("hepsi");
                      setOlay("");
                      setYalnizOkunmamis(false);
                    }}
                  >
                    Süzgeci temizle
                  </Buton>
                }
              />
            ) : (
              <BosDurum
                ikon="bos"
                baslik={yalnizOkunmamis ? METIN[kip].okunmamisYok : METIN[kip].bosBaslik}
                aciklama={METIN[kip].bosAciklama}
              />
            )
          ) : (
            <ul className="flex flex-col gap-2">
              {gorunen.map((b) => (
                <li key={b.id}>
                  <BildirimSatiri
                    bildirim={b}
                    onOkundu={() => okundu.mutate(b.id)}
                    isleniyor={okundu.isPending && okundu.variables === b.id}
                    onGit={onGit}
                  />
                </li>
              ))}
            </ul>
          )}

          {/*
            "DAHA ESKİSİNİ YÜKLE" SÜZGEÇTEN BAĞIMSIZ: süzgeç sonucu
            boş çıkmış olsa bile görünüyor, çünkü aranan bildirim daha
            eski bir sayfada olabilir. Düğmeyi yalnızca liste doluyken
            göstermek, "sonuç yok" ekranında kullanıcıyı çıkışsız
            bırakırdı.
          */}
          {liste.hasNextPage && (
            <div>
              <Buton
                gorunum="ikincil"
                yukleniyor={liste.isFetchingNextPage}
                onClick={() => void liste.fetchNextPage()}
              >
                Daha eskisini yükle
              </Buton>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function BildirimSatiri({
  bildirim,
  onOkundu,
  isleniyor,
  onGit,
}: {
  bildirim: Bildirim;
  onOkundu: () => void;
  isleniyor: boolean;
  onGit?: (hedef: { gorunum: "veli-rapor"; kayitId: string }) => void;
}) {
  const hedef = onGit ? bildirimHedefi(bildirim.linkUrl) : null;

  return (
    <div
      className={
        bildirim.isRead
          ? "rounded-kart border-notr-200 shadow-kart flex flex-col gap-2 border p-3"
          : // OKUNMAMIŞ AYRI GÖRÜNÜYOR: sol çubuk + zemin. Yalnızca
            // kalın yazı, telefonda uzun bir listede zayıf kalıyor.
            "rounded-kart shadow-kart border-marka-200 border-l-marka-600 bg-marka-50 flex flex-col gap-2 border border-l-4 p-3"
      }
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-notr-900 text-sm font-medium">{bildirim.title}</p>
          {bildirim.body && <p className="text-notr-700 mt-0.5 text-sm">{bildirim.body}</p>}
          <p className="text-notr-600 mt-1 text-xs">
            {bildirimOlayAdi(bildirim.eventCode)} · {tarihSaat(bildirim.createdAt)}
          </p>
        </div>
        {!bildirim.isRead && <Rozet ton="bilgi">Yeni</Rozet>}
      </div>

      <div className="flex flex-wrap gap-2">
        {/*
          YOL TANINMIYORSA DÜĞME DE YOK — panelin kendi dersi:
          "basılıp hiçbir şey olmayan bir düğme, olmayan bir düğmeden
          kötüdür." `bildirimHedefi` bu uygulamanın açabildiği tek
          yolu tanıyor, gerisine `null` dönüyor.
        */}
        {hedef && (
          <Buton
            gorunum="ikincil"
            onClick={() => {
              onGit!(hedef);
              if (!bildirim.isRead) onOkundu();
            }}
          >
            Kayda git
          </Buton>
        )}
        {!bildirim.isRead && (
          <Buton gorunum="sessiz" yukleniyor={isleniyor} onClick={onOkundu}>
            Okundu işaretle
          </Buton>
        )}
      </div>
    </div>
  );
}
